import { MailOutline, Monitor, Call } from '@mui/icons-material';
import { Box, Chip, Skeleton, Stack, SxProps, Typography } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { IconLink, IconTypography, Status } from '@procureai/ui-mui';
import { useTranslation } from 'react-i18next';

import { SupplierRelationshipType } from '@/generated/gql/graphql';
import { I18nNamespace } from '~/common/types';
import { mailToLink } from '~/util/string';

import { Root, ChipWrapper, Dot } from './SupplierMapDetailContent.styled';

const typeToColor: { [key in SupplierRelationshipType]: string } = {
  [SupplierRelationshipType.known]: 'secondary.main',
  [SupplierRelationshipType.unknown]: 'text.primary',
  [SupplierRelationshipType.partner]: 'secondary.main',
};

export interface SupplierMapDetailContentProps {
  sx?: SxProps<Theme>;
  loading?: boolean;
  data?: {
    id: string;
    name: string;
    relationship: SupplierRelationshipType;
    address: {
      countryCode?: string | null;
      city?: string | null;
      street?: string | null;
      postalCode?: string | null;
    };
    industry?: {
      id: string;
      name?: string | null;
    };
    subIndustries?: { id: string; name: string; color: string }[];
    contact?: {
      email?: string | null;
      fax?: string | null;
      website?: string | null;
      phone?: string | null;
    };
    prequalificationTrades?: {
      name: string;
      status: string;
    }[];
  };
}

export const SupplierMapDetailContent = ({ loading, data }: SupplierMapDetailContentProps) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'SupplierMapDetail' });
  const { t: tCountries } = useTranslation(I18nNamespace.countries);
  const { t: tType } = useTranslation(undefined, { keyPrefix: 'supplierType' });
  const { address, industry, subIndustries, contact, prequalificationTrades } = data || {};

  return (
    <Root>
      {loading ? (
        <>
          <Skeleton variant="text" />
          <Skeleton variant="text" />
          <Skeleton variant="text" />
          <Skeleton variant="text" width="75%" />
        </>
      ) : (
        <>
          {data?.relationship && (
            <Box
              sx={{
                mb: 3,
              }}
            >
              <Typography component="div" variant="caption" color="textSecondary">
                {t('type')}
              </Typography>
              <Status statusColor={typeToColor[data.relationship] || ''}>{tType(data.relationship)}</Status>
            </Box>
          )}

          {subIndustries && (
            <Box
              sx={{
                mb: 3,
              }}
            >
              <Typography component="div" variant="caption" color="textSecondary" gutterBottom>
                {t('subIndustries')}
              </Typography>
              <ChipWrapper>
                {subIndustries.map((item, index) => (
                  <Chip
                    key={index}
                    size="small"
                    icon={<Dot sx={{ '&::before': { bgcolor: item.color } }} />}
                    label={item.name}
                  />
                ))}
              </ChipWrapper>
            </Box>
          )}

          {address && (
            <Box
              sx={{
                mb: 3,
              }}
            >
              <Typography component="div" variant="caption" color="textSecondary">
                {t('contact')}
              </Typography>
              {address.street && <Typography variant="body1">{address.street}</Typography>}
              {(address.postalCode || address.city) && (
                <Typography variant="body1">
                  {address.postalCode} {address.city}
                </Typography>
              )}
              {address.countryCode && (
                <Typography variant="body1">{tCountries(address.countryCode.toUpperCase())}</Typography>
              )}
            </Box>
          )}

          {contact && (
            <Box
              sx={{
                mb: 3,
              }}
            >
              {contact.email && (
                <IconLink
                  mb={1}
                  underline="hover"
                  variant="body1"
                  color="text.primary"
                  startIcon={<MailOutline color="inherit" fontSize="small" />}
                  href={mailToLink(contact.email)}
                >
                  {contact.email}
                </IconLink>
              )}
              {contact.website && (
                <IconLink
                  mb={1}
                  underline="hover"
                  variant="body1"
                  color="text.primary"
                  startIcon={<Monitor color="inherit" fontSize="small" />}
                  href={contact.website}
                >
                  {contact.website}
                </IconLink>
              )}
              {contact.phone && (
                <IconTypography
                  variant="body1"
                  color="text.primary"
                  startIcon={<Call color="inherit" fontSize="small" />}
                >
                  {contact.phone}
                </IconTypography>
              )}
            </Box>
          )}

          {industry?.name && (
            <Box
              sx={{
                mb: 3,
              }}
            >
              <Typography component="div" variant="caption" color="textSecondary">
                {t('industry')}
              </Typography>
              <Typography variant="body1">{industry.name}</Typography>
            </Box>
          )}

          {prequalificationTrades && prequalificationTrades.length > 0 && (
            <Stack
              direction="column"
              sx={{
                mb: 3,
              }}
            >
              <Typography component="div" variant="caption" color="textSecondary">
                {t('prequalificationTrades')}
              </Typography>
              <ChipWrapper>
                {prequalificationTrades.map((trade) => (
                  <Chip
                    key={trade.name}
                    size="small"
                    icon={<Dot sx={{ '&::before': { bgcolor: trade.status } }} />}
                    label={trade.name}
                  />
                ))}
              </ChipWrapper>
            </Stack>
          )}
        </>
      )}
    </Root>
  );
};
